import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { Report as ReportModel, Track } from '../../../models';
import { Icon, FieldHelp } from '../..';
import { Stats } from '../Stats';
import moment from 'moment';

export type ReportingContext = 'user' | 'project' | 'client' | 'sprint' | 'goal';
export type WorkFilter = 'all' | 'dev' | 'non_dev' | 'support' | 'ops';

interface DashboardProps {
    report: ReportModel;
    tracks: Track[];
    context: ReportingContext;
    showAssignmentStock?: boolean;
    workFilter?: WorkFilter;
    onWorkFilterChange?: (filter: WorkFilter) => void;
}

type HeroKpi = {
    key: string;
    label: string;
    value: string;
    hint: string;
};

const FILTERS: WorkFilter[] = ['all', 'dev', 'non_dev', 'support', 'ops'];
const OPS_SUBTYPES = ['ops', 'oncall', 'abuse'];

function matchesWorkFilter(change: AnyObj | undefined, workFilter: WorkFilter) {
    if (workFilter === 'all')
        return true;
    if (!change)
        return false;
    const domain = change.typeDomain;
    const subtype = change.typeSubtype;
    if (workFilter === 'dev')
        return domain !== 'non_dev';
    if (workFilter === 'non_dev')
        return domain === 'non_dev';
    if (workFilter === 'support')
        return subtype === 'support';
    if (workFilter === 'ops')
        return OPS_SUBTYPES.includes(subtype);
    return true;
}

function formatHours(value?: number | null) {
    if (value == null || Number.isNaN(value))
        return '—';
    return `${Number(value).toFixed(1)} h`;
}

function formatPct(value?: number | null) {
    if (value == null || Number.isNaN(value))
        return '—';
    return `${Number(value).toFixed(0)} %`;
}

function formatHoursDuration(value?: number | null) {
    if (value == null || Number.isNaN(value))
        return '—';
    if (value < 48)
        return `${Number(value).toFixed(1)} h`;
    return `${(value / 24).toFixed(1)} j`;
}

function KpiTile({ label, value, hint }: { label: string; value: string; hint: string }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {label}
                </span>
                <FieldHelp content={hint} />
            </div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                {value}
            </div>
        </div>
    );
}

function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section className="rounded-xl border border-gray-200 bg-slate-50/80 dark:border-gray-700 dark:bg-gray-900/40">
            <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                onClick={() => setOpen(!open)}
            >
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <Icon type={open ? 'up' : 'down'} size={4} />
            </button>
            {open && <div className="border-t border-gray-200 px-4 py-4 dark:border-gray-700">{children}</div>}
        </section>
    );
}

function Dashboard(props: DashboardProps) {
    const {
        report,
        tracks,
        context,
        showAssignmentStock = context !== 'user',
        workFilter: controlledFilter,
        onWorkFilterChange,
    } = props;
    const { t } = useTranslation();
    const [internalFilter, setInternalFilter] = useState<WorkFilter>('all');
    const workFilter = controlledFilter ?? internalFilter;
    const setWorkFilter = onWorkFilterChange ?? setInternalFilter;

    const hint = (key: string) => t(`report.dashboard.hints.${key}`);

    // Keep charts in sync with the report work_filter (tracks list itself is unfiltered).
    const filteredTracks = useMemo(() => {
        if (workFilter === 'all')
            return tracks;
        return tracks.filter((track) => {
            const change: AnyObj = track.change || {};
            return matchesWorkFilter(change, workFilter);
        });
    }, [tracks, workFilter]);

    const heroKpis: HeroKpi[] = useMemo(() => {
        if (!report)
            return [];

        const activity = report.activity;
        const capacity = report.capacity;
        const tickets = report.tickets;
        const delivery = report.delivery;
        const billing = report.billing;

        if (context === 'user') {
            return [
                { key: 'tracked', label: t('report.dashboard.tracked'), value: formatHours(activity?.tracked_hours ?? report.tracks?.tracked_hours), hint: hint('tracked') },
                { key: 'fill', label: t('report.dashboard.fill_ratio'), value: formatPct(capacity?.fill_ratio ?? report.users?.tracking_ratio), hint: hint('fill_ratio') },
                {
                    key: 'dev_share',
                    label: t('report.dashboard.dev_share'),
                    value: formatPct(
                        activity ? (activity.tracked_hours ? (activity.by_domain.dev / activity.tracked_hours) * 100 : 0) : null
                    ),
                    hint: hint('dev_share'),
                },
                { key: 'resolved', label: t('report.dashboard.resolved'), value: String(tickets?.volumes.resolved ?? 0), hint: hint('resolved') },
                { key: 'lead', label: t('report.dashboard.lead_resolve_median'), value: formatHoursDuration(tickets?.lead_times.create_to_resolve.median), hint: hint('lead_resolve_median') },
                { key: 'top_project', label: t('report.dashboard.top_project'), value: activity?.top_projects?.[0]?.key || '—', hint: hint('top_project') },
            ];
        }

        if (context === 'sprint' || context === 'goal') {
            return [
                { key: 'remaining', label: t('report.dashboard.remaining'), value: formatHours(delivery?.to_deploy_hours ?? report.changes?.to_deploy_hours), hint: hint('remaining') },
                { key: 'completion', label: t('report.dashboard.completion'), value: formatPct(delivery?.deployment_ratio ?? report.changes?.deployment_ratio), hint: hint('completion') },
                { key: 'tracked', label: t('report.dashboard.tracked'), value: formatHours(activity?.tracked_hours ?? report.tracks?.tracked_hours), hint: hint('tracked') },
                { key: 'estimated', label: t('report.dashboard.estimated'), value: formatHours(delivery?.estimated_hours ?? report.changes?.estimated_hours), hint: hint('estimated') },
                { key: 'resolved', label: t('report.dashboard.resolved'), value: String(tickets?.volumes.resolved ?? 0), hint: hint('resolved') },
                { key: 'open', label: t('report.dashboard.open'), value: String(tickets?.stock.open ?? 0), hint: hint('open') },
            ];
        }

        return [
            { key: 'created', label: t('report.dashboard.created'), value: String(tickets?.volumes.created ?? 0), hint: hint('created') },
            { key: 'resolved', label: t('report.dashboard.resolved'), value: String(tickets?.volumes.resolved ?? 0), hint: hint('resolved') },
            { key: 'open', label: t('report.dashboard.open'), value: String(tickets?.stock.open ?? 0), hint: hint('open') },
            { key: 'lead', label: t('report.dashboard.lead_resolve_median'), value: formatHoursDuration(tickets?.lead_times.create_to_resolve.median), hint: hint('lead_resolve_median') },
            { key: 'tracked', label: t('report.dashboard.tracked'), value: formatHours(activity?.tracked_hours ?? report.tracks?.tracked_hours), hint: hint('tracked') },
            { key: 'billable', label: t('report.dashboard.billable_tracked'), value: formatHours(billing?.billable_tracked_hours ?? activity?.billable_hours), hint: hint('billable_tracked') },
        ];
    }, [report, context, t]);

    const ticketsSeries = useMemo(() => {
        const days = report?.tickets?.by_day || [];
        return {
            categories: days.map((d) => d.day),
            created: days.map((d) => d.created),
            accepted: days.map((d) => d.accepted),
            discarded: days.map((d) => d.discarded),
            resolved: days.map((d) => d.resolved),
        };
    }, [report]);

    if (!report)
        return null;

    const tickets = report.tickets;
    const support = report.support;
    const showSupport = !!support && ['all', 'support', 'non_dev'].includes(workFilter);
    const showDelivery = !!(report.activity || report.delivery || report.billing);
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
                {FILTERS.map((filter) => (
                    <button
                        key={filter}
                        type="button"
                        onClick={() => setWorkFilter(filter)}
                        className={`rounded-full border px-3 py-1 text-sm ${
                            workFilter === filter
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200'
                                : 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                    >
                        {t(`report.dashboard.filters.${filter}`)}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                {heroKpis.map((kpi) => (
                    <KpiTile key={kpi.key} label={kpi.label} value={kpi.value} hint={kpi.hint} />
                ))}
            </div>

            {tickets && (
                <Section title={t('report.dashboard.section_tickets')} defaultOpen={workFilter !== 'dev'}>
                    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
                        <KpiTile label={t('report.dashboard.created')} value={String(tickets.volumes.created)} hint={hint('created')} />
                        <KpiTile label={t('report.dashboard.accepted')} value={String(tickets.volumes.accepted)} hint={hint('accepted')} />
                        <KpiTile label={t('report.dashboard.discarded')} value={String(tickets.volumes.discarded)} hint={hint('discarded')} />
                        <KpiTile label={t('report.dashboard.resolved')} value={String(tickets.volumes.resolved)} hint={hint('resolved')} />
                        <KpiTile label={t('report.dashboard.open')} value={String(tickets.stock.open)} hint={hint('open')} />
                        <KpiTile label={t('report.dashboard.net_intake')} value={String(tickets.volumes.net_intake)} hint={hint('net_intake')} />
                        {showAssignmentStock && (
                            <KpiTile
                                label={t('report.dashboard.assignment_ratio')}
                                value={formatPct(tickets.stock.assignment_ratio)}
                                hint={hint('assignment_ratio')}
                            />
                        )}
                        <KpiTile
                            label={t('report.dashboard.assign_lead_median')}
                            value={formatHoursDuration(tickets.lead_times.create_to_assign.median)}
                            hint={hint('assign_lead_median')}
                        />
                        <KpiTile label={t('report.dashboard.in_triage')} value={String(tickets.stock.in_triage)} hint={hint('in_triage')} />
                        <KpiTile label={t('report.dashboard.aging_7')} value={String(tickets.stock.aging_over_7_days)} hint={hint('aging_7')} />
                        <KpiTile label={t('report.dashboard.wip')} value={String(tickets.stock.wip)} hint={hint('wip')} />
                        <KpiTile
                            label={t('report.dashboard.refuse_ratio')}
                            value={formatPct(tickets.volumes.refuse_ratio)}
                            hint={hint('refuse_ratio')}
                        />
                    </div>
                    {ticketsSeries.categories.length > 0 && (
                        <Chart
                            type="area"
                            height={280}
                            width="100%"
                            series={[
                                { name: t('report.dashboard.created') || '', data: ticketsSeries.created },
                                { name: t('report.dashboard.accepted') || '', data: ticketsSeries.accepted },
                                { name: t('report.dashboard.discarded') || '', data: ticketsSeries.discarded },
                                { name: t('report.dashboard.resolved') || '', data: ticketsSeries.resolved },
                            ]}
                            options={{
                                chart: { toolbar: { show: false }, zoom: { enabled: false } },
                                stroke: { curve: 'smooth', width: 2 },
                                dataLabels: { enabled: false },
                                xaxis: {
                                    categories: ticketsSeries.categories,
                                    labels: { formatter: (v: string) => moment(v).format('DD/MM') },
                                },
                                legend: { position: 'bottom' },
                            }}
                        />
                    )}
                </Section>
            )}

            {showSupport && (
                <Section title={t('report.dashboard.section_support')} defaultOpen={workFilter === 'support' || workFilter === 'non_dev'}>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <KpiTile
                            label={t('report.dashboard.support_triage')}
                            value={String(support.pendingTriage?.count ?? 0)}
                            hint={hint('support_triage')}
                        />
                        <KpiTile
                            label={t('report.dashboard.support_triage_age')}
                            value={formatHoursDuration(support.pendingTriage?.ageHoursAvg)}
                            hint={hint('support_triage_age')}
                        />
                        <KpiTile
                            label={t('report.dashboard.support_created_today')}
                            value={String(support.createdToday?.count ?? 0)}
                            hint={hint('support_created_today')}
                        />
                        <KpiTile
                            label={t('report.dashboard.support_unanswered')}
                            value={String(support.unanswered?.count ?? 0)}
                            hint={hint('support_unanswered')}
                        />
                    </div>
                </Section>
            )}

            {showDelivery && (
                <Section title={t('report.dashboard.section_delivery')} defaultOpen={context === 'sprint' || context === 'goal'}>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <KpiTile label={t('report.dashboard.estimated')} value={formatHours(report.delivery?.estimated_hours)} hint={hint('estimated')} />
                        <KpiTile label={t('report.dashboard.deployed')} value={formatHours(report.delivery?.deployed_hours)} hint={hint('deployed')} />
                        <KpiTile label={t('report.dashboard.remaining')} value={formatHours(report.delivery?.to_deploy_hours)} hint={hint('remaining')} />
                        <KpiTile label={t('report.dashboard.billable_estimated')} value={formatHours(report.billing?.billable_estimated_hours)} hint={hint('billable_estimated')} />
                        <KpiTile label={t('report.dashboard.capacity')} value={formatHours(report.capacity?.capacity_hours)} hint={hint('capacity')} />
                        <KpiTile label={t('report.dashboard.fill_ratio')} value={formatPct(report.capacity?.fill_ratio)} hint={hint('fill_ratio')} />
                        <KpiTile label={t('report.dashboard.dev_hours')} value={formatHours(report.activity?.by_domain.dev)} hint={hint('dev_hours')} />
                        <KpiTile label={t('report.dashboard.non_dev_hours')} value={formatHours(report.activity?.by_domain.non_dev)} hint={hint('non_dev_hours')} />
                    </div>
                </Section>
            )}

            <Section title={t('report.dashboard.section_charts')} defaultOpen>
                <Stats tracks={filteredTracks} report={report} />
            </Section>
        </div>
    );
}

export default Dashboard;
